import React, { forwardRef, useId } from 'react';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const VARIANTS = {
  outline:
    'bg-white border border-gray-300 focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  filled:
    'bg-gray-50 border border-gray-300 focus:bg-white focus:border-blue-600 focus:ring-2 focus:ring-blue-600/20',
  unstyled:
    'bg-transparent border-transparent focus:border-transparent focus:ring-0',
};

const SIZE = {
  sm: 'h-9 text-sm',
  md: 'h-10 text-sm',
  lg: 'h-12 text-base',
};

const BASE =
  'block w-full rounded-md transition-colors placeholder:text-gray-400 focus:outline-none';
const DISABLED =
  'disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed';

const Dropdown = forwardRef(function Dropdown(
  {
    label,
    helperText,
    error,
    required = false,
    name,
    id: idProp,
    value,
    defaultValue,
    onChange,           
    onValueChange,      
    options = [],
    placeholder,
    variant = 'outline',
    size = 'md',
    fullWidth = true,
    leftIcon,
    rightIcon,
    prefix,
    suffix,
    disabled = false,
    loading = false,
    clearable = false,
    onClear,
    className,
    selectClassName,
    containerClassName,
    style,
    ...rest
  },
  ref
) {
  const reactId = useId();
  const id = idProp || `${name || 'dropdown'}-${reactId}`;

  const describedBy = [];
  if (helperText) describedBy.push(`${id}-help`);
  if (error) describedBy.push(`${id}-error`);

  const sizeClasses = SIZE[size];
  const variantClasses = VARIANTS[variant];
  const errorClasses = error
    ? 'border-red-500 focus:border-red-600 focus:ring-red-600/20'
    : '';

  const withLeft = leftIcon || prefix;
  const withRight = rightIcon || suffix || loading || clearable;

  const paddingLeft = withLeft ? 'pl-10' : '';
  const paddingRight = withRight ? 'pr-10' : '';

  const selectClasses = cn(
    BASE,
    sizeClasses,
    variantClasses,
    DISABLED,
    paddingLeft,
    paddingRight,
    errorClasses,
    selectClassName
  );

  const emitChange = (event, nextValue) => {
    if (onChange) onChange(event, nextValue);
    if (onValueChange) onValueChange(nextValue);
  };

  const handleNativeChange = (e) => {
    const next = e?.target?.value ?? '';
    emitChange(e, next);
  };

  const handleClear = (e) => {
    e?.preventDefault?.();
    if (onClear) onClear();
    emitChange(undefined, '');
  };

  const hasValue =
    value !== undefined && value !== null && `${value}`.length > 0;

  return (
    <div
      className={cn(fullWidth ? 'w-full' : '', containerClassName, className)}
      style={style}
    >
      {label && (
        <label
          htmlFor={id}
          className="mb-1.5 block text-sm font-medium text-gray-700"
        >
          {label}
          {required && <span className="ml-0.5 text-red-600">*</span>}
        </label>
      )}

      <div className="relative flex items-stretch w-full">
        {leftIcon && (
          <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
            {leftIcon}
          </span>
        )}

        {prefix && (
          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
            {prefix}
          </span>
        )}

        <select
          id={id}
          name={name}
          ref={ref}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          onChange={handleNativeChange}
          aria-invalid={!!error || undefined}
          aria-describedby={describedBy.length ? describedBy.join(' ') : undefined}
          className={selectClasses}
          {...rest}
        >
          {placeholder && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}

          {options.map((option) =>
            typeof option === 'string' ? (
              <option key={option} value={option}>
                {option}
              </option>
            ) : (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            )
          )}
        </select>

        {loading && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
            Loading...
          </span>
        )}

        {!loading && clearable && hasValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
            aria-label="Clear selection"
            tabIndex={-1}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" stroke="currentColor" fill="none">
              <path d="M6 6l12 12M18 6L6 18" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        )}

        {!loading && !clearable && rightIcon && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400">
            {rightIcon}
          </span>
        )}

        {!loading && !clearable && !rightIcon && suffix && (
          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-500">
            {suffix}
          </span>
        )}
      </div>

      {helperText && !error && (
        <p id={`${id}-help`} className="mt-1 text-xs text-gray-500">
          {helperText}
        </p>
      )}

      {error && (
        <p id={`${id}-error`} className="mt-1 text-xs text-red-600">
          {typeof error === 'string' ? error : 'Invalid selection'}
        </p>
      )}
    </div>
  );
});

export default Dropdown;